/**
 * Which meeting app makes the link for an online appointment — the answer the
 * Integrate apps card sets and the Schedule dialog reads.
 *
 * Backed by TanStack Query and shared, the way `useDishScope` is: one setting
 * for the workspace, so the card on My brand and a dialog open on a client
 * read the same cached answer and a change on one is on both.
 *
 * Two different questions live here, and the card presses two different
 * buttons for them:
 *
 *   linked    an account is on file with that provider. Costs a trip out to
 *             Google or Zoom and back.
 *   enabled   that provider is what makes the links. A field on the
 *             workspace, flipped by `PATCH /meeting-settings/` and flipped
 *             back the same way, with no sign-in either time.
 *
 * So a workspace that has linked an account and turned that provider off is
 * one press from turning it on again, and `connect` below does that press
 * rather than sending somebody back out for an account the backend already
 * holds.
 *
 * The two questions have different shapes, because the wire does: accounts
 * are a list and both can be linked at once, while the setting is one field
 * and holds one provider. Which means turning Zoom on is what turns Google
 * Meet off, and the card needs no second press to do it — see `connect`.
 */

import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchMeetingSettings,
  saveMeetingProvider,
  startProviderConnect,
} from "../services/meetingSettingsService";
import { APP_SLUGS, MEETING_APPS, type MeetingAppId } from "../meetingApps";
import type {
  MeetingProviderCode,
  MeetingProviderLink,
  MeetingSettings,
} from "../types/appointments.types";

export const MEETING_SETTINGS_QUERY_KEY = [
  "appointments",
  "meeting-settings",
] as const;

/**
 * The apps this screen draws, against the `meeting_provider` the backend
 * sets. Separate from `APP_SLUGS` because they are two different names for
 * the same thing on two different parts of the wire — a slug addresses a
 * provider in a URL, a code says which one is in use. Here rather than beside
 * the slugs because only this file sends a code.
 */
const PROVIDER_CODES: Record<MeetingAppId, MeetingProviderCode> = {
  "google-meet": "GOOGLE_MEET",
  zoom: "ZOOM",
};

/**
 * Which flag on the settings answers for which app.
 *
 * Written out rather than compared against `provider`, because the backend
 * sends both the field and the flags and these are what it said. Where it
 * leaves a flag off, the service has already filled it in from the field —
 * see `toMeetingSettings` — so by the time they get here the two agree.
 */
const ENABLED: Record<MeetingAppId, (settings: MeetingSettings) => boolean> = {
  "google-meet": (settings) => settings.googleMeetEnabled,
  zoom: (settings) => settings.zoomEnabled,
};

export interface MeetingAppsState {
  /** `undefined` until the first read lands. */
  settings?: MeetingSettings;
  /** True while nothing has been read yet — the card draws a placeholder. */
  loading: boolean;
  /** Whether this app is the one making links. */
  isEnabled: (app: MeetingAppId) => boolean;
  /** Whether an account is on file for it, enabled or not. */
  isLinked: (app: MeetingAppId) => boolean;
  /** Whose account, where the backend named one. */
  accountEmail: (app: MeetingAppId) => string | null;
  /**
   * Whether a link gets made for an online appointment at all — the one thing
   * the Schedule dialog asks before it decides whether to offer connecting one.
   */
  anyConnected: boolean;
  /**
   * Which app is making them, or `null` while none is. The Schedule dialog
   * wears its mark on the Online button, so that choosing Online says which
   * app the client will be sent to rather than leaving it to be discovered
   * once the invitation arrives.
   */
  activeApp: MeetingAppId | null;
  /**
   * Turns an app on. Where no account is linked yet this answers with that
   * provider's sign-in URL for the caller to send the browser to; where one
   * is, it flips the setting and answers `null`.
   *
   * It is also how the other provider gets turned off, because the setting is
   * one field: connecting Zoom on a workspace running Google Meet is one
   * press, not a disconnect and then a connect.
   */
  connect: (app: MeetingAppId) => Promise<string | null>;
  /**
   * Turns it on without asking whether an account is linked — for the one
   * caller that already knows: the page the provider's redirect lands on,
   * which is there because an account has just been linked. `connect` cannot
   * be used there. It reads the settings to decide, and on a page opened by a
   * redirect they have not been read yet, so it would send the browser
   * straight back out for an account the backend is already holding.
   */
  enable: (app: MeetingAppId) => Promise<void>;
  /**
   * Turns it off, leaving the linked account alone. A no-op where the app
   * asked about is not the one that is on: `MANUAL` is not addressed to a
   * provider, so sending it for a row that was already off would turn the
   * other one off instead.
   */
  disconnect: (app: MeetingAppId) => Promise<void>;
  /** Reads the settings again — what the callback page calls on its way in. */
  refresh: () => Promise<unknown>;
}

const providerOf = (
  settings: MeetingSettings | undefined,
  app: MeetingAppId
): MeetingProviderLink | undefined =>
  settings?.providers.find((provider) => provider.slug === APP_SLUGS[app]);

const isEnabledIn = (
  settings: MeetingSettings | undefined,
  app: MeetingAppId
): boolean => !!settings && ENABLED[app](settings);

export function useMeetingApps(): MeetingAppsState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: MEETING_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => fetchMeetingSettings(getToken, signal),
  });

  const settings = query.data;

  // Logged, not shown. A workspace that cannot read this setting is one whose
  // Schedule dialog asks for the link to be connected — the same thing it
  // says to a workspace that never connected one — and an error banner over
  // the brand screen would be shouting about a setting most people never
  // touch. The console is where the reason goes, as `useDishScope` does.
  useEffect(() => {
    if (query.isError) {
      console.error("Meeting settings failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  // Both writes answer with the settings as they now stand, so what came back
  // is written into the cache rather than what was asked for: the backend is
  // the one that decides, and a card drawing its own request would be telling
  // the user what it hoped had happened.
  const store = useCallback(
    (next: MeetingSettings) => {
      queryClient.setQueryData<MeetingSettings>(MEETING_SETTINGS_QUERY_KEY, next);
    },
    [queryClient]
  );

  const enable = useCallback(
    async (app: MeetingAppId): Promise<void> => {
      store(await saveMeetingProvider(getToken, PROVIDER_CODES[app]));
    },
    [getToken, store]
  );

  const connect = useCallback(
    async (app: MeetingAppId): Promise<string | null> => {
      // An account already on file needs no second trip out to the provider:
      // the setting is flipped where it stands. True of a workspace running
      // the other provider too — the accounts are a list and the setting is
      // one field, so all that is left to do is the field.
      if (providerOf(settings, app)?.linked) {
        await enable(app);
        return null;
      }

      return startProviderConnect(getToken, APP_SLUGS[app]);
    },
    [enable, getToken, settings]
  );

  const disconnect = useCallback(
    async (app: MeetingAppId): Promise<void> => {
      // Only the row that is on can turn anything off. `MANUAL` names no
      // provider, so sending it for a row that was already off would turn the
      // other one off instead — and the card draws Disconnect on one row at a
      // time, so this is a guard rather than a path.
      if (!isEnabledIn(settings, app)) return;
      store(await saveMeetingProvider(getToken, "MANUAL"));
    },
    [getToken, settings, store]
  );

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: MEETING_SETTINGS_QUERY_KEY }),
    [queryClient]
  );

  return {
    settings,
    loading: query.isPending,
    isEnabled: (app) => isEnabledIn(settings, app),
    isLinked: (app) => !!providerOf(settings, app)?.linked,
    accountEmail: (app) => providerOf(settings, app)?.accountEmail ?? null,
    // Nothing is assumed while the read is in flight: a dialog that hid its
    // Connect strip on the strength of a setting it has not read yet would
    // show one for a moment on every workspace that has none. Either provider
    // answers this one — what the dialog asks is whether a link gets made at
    // all, not which app makes it.
    anyConnected: !!settings && settings.provider !== "MANUAL",
    activeApp:
      MEETING_APPS.find(
        (app) => !!settings && PROVIDER_CODES[app.id] === settings.provider
      )?.id ?? null,
    connect,
    enable,
    disconnect,
    refresh,
  };
}

export default useMeetingApps;
